import { useState } from "react";
import { useCAPartner } from "@/contexts/CAPartnerContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Wallet, Banknote, CreditCard, CheckCircle2, Clock, ArrowUpCircle } from "lucide-react";

const payoutStatusCfg = {
  requested: { label: "Requested", color: "bg-accent/10 text-accent border-accent/20", icon: Clock },
  processing: { label: "Processing", color: "bg-warning/10 text-warning border-warning/20", icon: ArrowUpCircle },
  completed: { label: "Completed", color: "bg-success/10 text-success border-success/20", icon: CheckCircle2 },
};

export default function PartnerPayoutSection() {
  const { payouts, stats, requestPayout } = useCAPartner();
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<"bank_transfer" | "platform_credits">("bank_transfer");

  const handlePayout = () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (amt > stats.pendingBalance) { toast.error("Insufficient balance"); return; }
    const ok = requestPayout(amt, method);
    if (ok) {
      toast.success(`Payout of ₹${amt} requested via ${method === "bank_transfer" ? "bank transfer" : "platform credits"}`);
      setAmount("");
    }
  };

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card space-y-4">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Wallet className="h-4 w-4 text-accent" /> Payouts
      </h3>

      {/* Request Payout */}
      <div className="p-3 rounded-lg bg-muted/20 border border-border space-y-3">
        <p className="text-xs font-semibold text-muted-foreground">Request Payout — Available: ₹{stats.pendingBalance}</p>
        <div className="flex gap-2">
          <button
            onClick={() => setMethod("bank_transfer")}
            className={`flex-1 p-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
              method === "bank_transfer" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent/40"
            }`}
          >
            <Banknote className="h-3.5 w-3.5" /> Bank Transfer
          </button>
          <button
            onClick={() => setMethod("platform_credits")}
            className={`flex-1 p-2 rounded-lg border text-xs font-medium flex items-center justify-center gap-1.5 transition-all ${
              method === "platform_credits" ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground hover:border-accent/40"
            }`}
          >
            <CreditCard className="h-3.5 w-3.5" /> Platform Credits
          </button>
        </div>
        <div className="flex gap-2">
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount" min={1} className="flex-1" />
          <Button onClick={handlePayout} disabled={!amount} size="sm">Request</Button>
        </div>
      </div>

      {/* Payout History */}
      <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
        {payouts.map((po) => {
          const cfg = payoutStatusCfg[po.status];
          const StatusIcon = cfg.icon;
          return (
            <div key={po.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/20 border border-border/50">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">₹{po.amount} — {po.method === "bank_transfer" ? "Bank Transfer" : "Platform Credits"}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(po.requestedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  {po.transactionRef && ` · ${po.transactionRef}`}
                </p>
              </div>
              <Badge variant="outline" className={`text-[10px] ${cfg.color} flex items-center gap-1`}>
                <StatusIcon className="h-2.5 w-2.5" /> {cfg.label}
              </Badge>
            </div>
          );
        })}
        {payouts.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">No payouts yet</p>
        )}
      </div>
    </div>
  );
}
